import { useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { ShieldCheck, X } from 'lucide-react'
import { useCookieConsentContext } from '@/context/CookieConsentContext'
import {
  COOKIE_CATEGORIES,
  type CookieCategory,
  type CookiePreferences,
} from '@/types/cookieConsent'
import { CookieButton, CookieSwitch } from '@components/CookieBanner/shared'

const OPTIONAL_CATEGORIES = COOKIE_CATEGORIES.filter((c) => !c.required)

const CookiePreferencesModal = () => {
  const {
    showModal,
    preferences,
    acceptAll,
    closePreferences,
    savePreferences,
  } = useCookieConsentContext()

  const [draft, setDraft] = useState<CookiePreferences>(preferences)
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (showModal) {
      setDraft(preferences)
      const timer = window.setTimeout(() => closeButtonRef.current?.focus(), 50)
      return () => window.clearTimeout(timer)
    }
  }, [showModal, preferences])

  useEffect(() => {
    if (!showModal) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePreferences()
      }

      if (event.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
        if (focusable.length === 0) return

        const first = focusable[0]
        const last = focusable[focusable.length - 1]

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }

    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [showModal, closePreferences])

  const toggleCategory = useCallback((category: CookieCategory) => {
    if (category === 'essential') return
    setDraft((prev) => ({ ...prev, [category]: !prev[category] }))
  }, [])

  const handleSave = () => {
    savePreferences(draft)
  }

  return (
    <AnimatePresence>
      {showModal && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-end justify-center p-0 sm:items-center sm:p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          aria-hidden={!showModal}
        >
          <motion.button
            type="button"
            aria-label="Fechar configurações de cookies"
            className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closePreferences}
          />

          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-modal-title"
            aria-describedby="cookie-modal-description"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-[1] flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-[22px] sm:rounded-[22px]"
            style={{
              background: 'var(--sp-surface)',
              border: '1px solid var(--sp-border)',
              boxShadow: 'var(--sp-panel-shadow)',
            }}
          >
            <div
              className="flex items-start justify-between gap-4 border-b px-5 py-5 sm:px-6"
              style={{ borderColor: 'var(--sp-border-2)' }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px]"
                  style={{ background: 'var(--sp-chip)' }}
                  aria-hidden="true"
                >
                  <ShieldCheck className="h-5 w-5" style={{ color: 'var(--sp-blue)' }} />
                </div>
                <div>
                  <h2
                    id="cookie-modal-title"
                    className="font-display text-xl font-bold tracking-tight"
                    style={{ color: 'var(--sp-text)' }}
                  >
                    Preferências de cookies
                  </h2>
                  <p
                    id="cookie-modal-description"
                    className="mt-1 text-[14px] leading-relaxed"
                    style={{ color: 'var(--sp-muted)' }}
                  >
                    Gerencie as categorias de cookies conforme a LGPD. Cookies essenciais permanecem
                    sempre ativos.
                  </p>
                </div>
              </div>

              <button
                ref={closeButtonRef}
                type="button"
                onClick={closePreferences}
                aria-label="Cancelar e fechar"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px] transition-colors hover:bg-[var(--sp-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sp-blue)]"
              >
                <X className="h-5 w-5" style={{ color: 'var(--sp-muted)' }} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6">
              <ul className="space-y-3" role="list">
                {COOKIE_CATEGORIES.map((category) => {
                  const isEssential = category.required
                  const enabled = isEssential ? true : draft[category.id as keyof CookiePreferences]

                  return (
                    <li
                      key={category.id}
                      className="rounded-[16px] p-4"
                      style={{
                        background: 'var(--sp-surface-2)',
                        border: '1px solid var(--sp-border-2)',
                      }}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3
                              className="text-[15px] font-semibold"
                              style={{ color: 'var(--sp-text)' }}
                            >
                              {category.title}
                            </h3>
                            {isEssential && (
                              <span
                                className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
                                style={{
                                  background: 'var(--sp-chip)',
                                  color: 'var(--sp-blue)',
                                }}
                              >
                                Sempre ativo
                              </span>
                            )}
                          </div>
                          <p
                            className="mt-2 text-[13px] leading-relaxed sm:text-[14px]"
                            style={{ color: 'var(--sp-muted)' }}
                          >
                            {category.description}
                          </p>
                        </div>

                        <CookieSwitch
                          id={`cookie-${category.id}`}
                          label={`${enabled ? 'Desativar' : 'Ativar'} ${category.title}`}
                          checked={Boolean(enabled)}
                          disabled={isEssential}
                          onChange={() => toggleCategory(category.id)}
                        />
                      </div>
                    </li>
                  )
                })}
              </ul>

              {OPTIONAL_CATEGORIES.length > 0 && (
                <p className="mt-4 text-[12px] leading-relaxed" style={{ color: 'var(--sp-muted)' }}>
                  Ferramentas como Google Analytics, Google Tag Manager, Meta Pixel e Microsoft Clarity
                  serão carregadas apenas quando você consentir com as categorias correspondentes.
                </p>
              )}
            </div>

            <div
              className="flex flex-col-reverse gap-2.5 border-t px-5 py-4 sm:flex-row sm:justify-end sm:px-6"
              style={{ borderColor: 'var(--sp-border-2)' }}
            >
              <CookieButton variant="ghost" onClick={closePreferences} className="w-full sm:w-auto">
                Cancelar
              </CookieButton>
              <CookieButton variant="secondary" onClick={handleSave} className="w-full sm:w-auto">
                Salvar preferências
              </CookieButton>
              <CookieButton variant="primary" onClick={acceptAll} className="w-full sm:w-auto">
                Aceitar todos
              </CookieButton>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default CookiePreferencesModal
